/**
 * Journey Routes
 *
 * New clean route structure:
 * - /journey/:journeyId/:referral - Main journey (with optional ?preview=key&lang=code for preview mode)
 * - /journey/:journeyId/:referral/end - Journey completion
 * - /journey/:journeyId/:referral/notinterested/:submissionId?/:submissionRandom? - Not interested page
 * - /submission/:submissionId - Resume submission
 *
 * Query params:
 * - ?preview=<previewKey>&lang=<languageCode> - Preview mode
 * - ?step=1&page=1&module=1 - Current position in journey
 * - ?name=<seo-friendly-name> - Optional SEO-friendly journey name
 */

// Route name constants
export const JOURNEY_ROUTE_NAMES = {
   JOURNEY: 'journey',
   JOURNEY_QUESTION: 'journey-question',
   JOURNEY_END: 'journey-end',
   JOURNEY_DECLINE: 'journey-decline',
   SUBMISSION: 'submission',
} as const;

/**
 * Parse the old referral param format
 * Old format: "preview~previewKey~languageCode" or just "referral-code"
 * Returns: { referral: string, query: { preview?: string, lang?: string } }
 */
function parseReferralParam(referralParam: string): { referral: string; query: Record<string, string> } {
   if (!referralParam) return { referral: '', query: {} };

   const parts = referralParam.split('~');

   // Check if it's a preview referral (starts with "preview")
   if (parts[0] === 'preview' && parts.length >= 2) {
      return {
         referral: 'preview',
         query: {
            preview: parts[1],
            ...(parts[2] ? { lang: parts[2] } : {}),
         },
      };
   }

   // Regular referral code (no parsing needed)
   return { referral: referralParam, query: {} };
}

const journeyRoutes = [
   // ============================================
   // NEW CLEAN ROUTES
   // ============================================

   // Main journey route
   {
      path: '/journey/:journeyId/:referral',
      name: JOURNEY_ROUTE_NAMES.JOURNEY,
      component: () => import('@/modules/journeys/pages/Journey.vue'),
      meta: { title: 'Journey' },
      beforeEnter: (to: any, from: any, next: any) => {
         // Handle legacy preview~key~lang format in referral param
         const { referral, query } = parseReferralParam(to.params.referral as string);
         if (Object.keys(query).length > 0) {
            // Redirect with preview params as query params
            next({
               name: JOURNEY_ROUTE_NAMES.JOURNEY,
               params: { ...to.params, referral },
               query: { ...to.query, ...query },
            });
         } else {
            next();
         }
      },
   },

   // Journey with question progress (same component, different name for navigation tracking)
   {
      path: '/journey/:journeyId/:referral',
      name: JOURNEY_ROUTE_NAMES.JOURNEY_QUESTION,
      component: () => import('@/modules/journeys/pages/Journey.vue'),
      meta: { title: 'Journey' },
      beforeEnter: (to: any, from: any, next: any) => {
         // Handle legacy preview~key~lang format in referral param
         const { referral, query } = parseReferralParam(to.params.referral as string);
         if (Object.keys(query).length > 0) {
            // Redirect with preview params as query params
            next({
               name: JOURNEY_ROUTE_NAMES.JOURNEY_QUESTION,
               params: { ...to.params, referral },
               query: { ...to.query, ...query },
            });
         } else {
            next();
         }
      },
   },

   // Journey end/completion page
   {
      path: '/journey/:journeyId/:referral/end',
      name: JOURNEY_ROUTE_NAMES.JOURNEY_END,
      component: () => import('@/modules/journeys/pages/JourneyEnd.vue'),
      meta: { title: 'Journey Complete' },
      beforeEnter: (to: any, from: any, next: any) => {
         // Handle legacy preview~key~lang format in referral param
         const { referral, query } = parseReferralParam(to.params.referral as string);
         if (Object.keys(query).length > 0) {
            // Redirect with preview params as query params
            next({
               name: JOURNEY_ROUTE_NAMES.JOURNEY_END,
               params: { ...to.params, referral },
               query: { ...to.query, ...query },
            });
         } else {
            next();
         }
      },
   },

   // Not interested / decline page
   {
      path: '/journey/:journeyId/:referral/notinterested/:submissionId?/:submissionRandom?',
      name: JOURNEY_ROUTE_NAMES.JOURNEY_DECLINE,
      component: () => import('@/modules/journeys/pages/NotInterested.vue'),
      meta: { title: 'Not Interested' },
   },

   // Submission resume route
   {
      path: '/submission/:submissionId',
      name: JOURNEY_ROUTE_NAMES.SUBMISSION,
      component: () => import('@/modules/journeys/pages/Submission.vue'),
      meta: { title: 'Resume' },
   },

   // ============================================
   // LEGACY REDIRECTS (for backwards compatibility)
   // ============================================

   // Legacy candidatejourney route
   {
      path: '/candidatejourney/:id',
      redirect: (to: any) => ({
         name: JOURNEY_ROUTE_NAMES.SUBMISSION,
         params: { submissionId: to.params.id },
      }),
   },
];

export default journeyRoutes;

